
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ContactCard from './ContactCard';
import TranslatedText from '@/TranslatedText';

const ContactSection = () => {
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const { toast } = useToast();

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, you would send this data to a backend API
    toast({
      title: "Support request submitted",
      description: "We'll get back to you within 24 hours.",
      variant: "default",
    });
    setContactName('');
    setContactEmail('');
    setContactSubject('');
    setContactMessage('');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ContactCard 
          icon={<Phone size={24} />}
          title={<TranslatedText text="Phone Support" />}
          content={<TranslatedText text="Available Monday to Friday, 9am to a6pm" />}
          action="+1 (555) 123-4567"
          buttonText={<TranslatedText text="Call Us" />}
          href="tel:+15551234567"
        />
        <ContactCard 
          icon={<Mail size={24} />}
          title={<TranslatedText text="Email Support" />}
          content={<TranslatedText text="We'll respond within 24 hours" />}
          action="support@ahavex.com"
          buttonText={<TranslatedText text="Email Us" />}
          href="mailto:support@ahavex.com"
        />
        <ContactCard 
          icon={<MessageCircle size={24} />}
          title={<TranslatedText text="Live Chat" />}
          content={<TranslatedText text="Chat with our support team in real-time" />}
          action={<TranslatedText text="Available 24/7" />}
          buttonText={<TranslatedText text="Start Chat" />}
          onClick={() => {
            toast({
              title: "Live chat",
              description: "Live chat feature would open here",
            });
          }}
        />
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle><TranslatedText text="Send us a message" /></CardTitle>
          <CardDescription>
            <TranslatedText text="Fill out the form below and we'll get back to you as soon as possible." />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmitForm} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  <TranslatedText text="Your Name" />
                </label>
                <Input 
                  id="name" 
                  placeholder="John Doe" 
                  value={contactName} 
                  onChange={(e) => setContactName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  <TranslatedText text="Email Address" />
                </label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="john@example.com" 
                  value={contactEmail} 
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="subject" className="text-sm font-medium">
                <TranslatedText text="Subject" />
              </label>
              <Input 
                id="subject" 
                placeholder="How can we help you?" 
                value={contactSubject} 
                onChange={(e) => setContactSubject(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="message" className="text-sm font-medium">
                <TranslatedText text="Message" />
              </label>
              <Textarea 
                id="message" 
                placeholder="Please describe your issue in detail..." 
                rows={5}
                value={contactMessage} 
                onChange={(e) => setContactMessage(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full md:w-auto">
              <TranslatedText text="Submit Request" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactSection;
